/**
 * Safe math expression evaluator for KPI formulas.
 * No eval() — uses a recursive descent parser.
 *
 * Supported syntax:
 *   Operands : KPI codes (e.g. GHG002, SGX-101, S-1) and numeric constants
 *   Operators: +  -  *  /  ( )
 *
 * Shared with ui/src/features/esg/utils/formula-evaluator.js
 * Keep both files byte-for-byte identical.
 */

// Matches codes like GHG001, SGX-101, S-1, optionally with a baseline anchor.
// Also matches bracket-quoted codes like [123-123] or [#123123] which are used
// when a code starts with a digit, hash, or other character that would otherwise
// be ambiguous with operators or number literals.
// `GHG001@baseline` — meaning "this code's value at the disclosure's
// configured baselineYear" rather than at the cell's own year. The anchor
// keeps the formula syntax extension localised: `@baseline` becomes part
// of the token's value so the lookup is keyed `"GHG001@baseline"`.
const KPI_CODE_RE = /\[[^\]]+\]|\b[A-Z][\w-]*\d+(@baseline)?\b/g;

/**
 * Extract all KPI code references from a formula string. Anchored
 * references retain their `@baseline` suffix in the returned list.
 * @param {string} formula  e.g. "(GHG001+GHG005)@baseline-GHG001-GHG005"
 * @returns {string[]}      e.g. ["GHG001","GHG005","GHG001@baseline","GHG005@baseline"]
 */
export function extractReferences(formula) {
  if (!formula) return [];
  const matches = formula.match(KPI_CODE_RE) || [];
  // Strip brackets from bracket-quoted codes: [123-123] → "123-123"
  const refs = matches.map((m) => (m.startsWith("[") ? m.slice(1, -1) : m));
  return [...new Set(refs)];
}

/**
 * Strip a `@baseline` anchor from a code, returning the bare KPI code.
 * Useful for resolving the referenced disclosure (anchored or not, it's
 * the same Disclosure document) without leaking the anchor.
 */
export function stripAnchor(code) {
  return code.endsWith("@baseline") ? code.slice(0, -9) : code;
}

// ---------------------------------------------------------------------------
// Tokeniser
// ---------------------------------------------------------------------------

function tokenize(expr) {
  const tokens = [];
  let i = 0;
  while (i < expr.length) {
    const ch = expr[i];

    // whitespace / percent — skip
    if (ch === " " || ch === "\t" || ch === "%") {
      i++;
      continue;
    }

    // single-char operators / parens
    if ("+-*/()".includes(ch)) {
      tokens.push({ type: "op", value: ch });
      i++;
      continue;
    }

    // number (integer or decimal) — strictly validated; "100.50.30" → NaN token
    if (/[0-9.]/.test(ch)) {
      let numStr = "";
      while (i < expr.length && /[0-9.]/.test(expr[i])) {
        numStr += expr[i];
        i++;
      }
      // Number() rejects partial parses like "100.50.30" → NaN
      tokens.push({ type: "number", value: Number(numStr) });
      continue;
    }

    // Bracket-quoted code: [any-code-here]
    // Supports codes that start with digits, hashes, or contain characters that
    // would otherwise be ambiguous (e.g. [123-123], [#123123], [123_123]).
    if (ch === "[") {
      let code = "";
      i++; // skip '['
      while (i < expr.length && expr[i] !== "]") {
        code += expr[i++];
      }
      i++; // skip ']'
      if (code) tokens.push({ type: "code", value: code });
      continue;
    }

    // KPI code — uppercase letter, then letters/digits/hyphens (allows SGX-101, S-1)
    // A hyphen is only included if the next character is alphanumeric (not subtraction).
    // An optional `@baseline` suffix is absorbed into the token value so the
    // valueMap lookup remains a single key (`"GHG001@baseline"`).
    if (/[A-Z]/.test(ch)) {
      let code = "";
      while (i < expr.length) {
        const c = expr[i];
        if (/[A-Z0-9]/.test(c)) {
          code += c;
          i++;
        } else if (
          c === "-" &&
          i + 1 < expr.length &&
          /[A-Z0-9]/.test(expr[i + 1])
        ) {
          // Hyphen followed by alphanumeric — part of the code, not subtraction
          code += c;
          i++;
        } else {
          break;
        }
      }
      if (expr.slice(i, i + 9) === "@baseline") {
        code += "@baseline";
        i += 9;
      }
      tokens.push({ type: "code", value: code });
      continue;
    }

    // unknown character — skip
    i++;
  }
  return tokens;
}

// ---------------------------------------------------------------------------
// Recursive-descent parser   (expr → term → factor → atom)
//
//   expr   = term (('+' | '-') term)*
//   term   = factor (('*' | '/') factor)*
//   factor = ('+' | '-')? atom
//   atom   = NUMBER | CODE | '(' expr ')'
//
// Failure propagation: instead of a mutable "missing" side-channel flag,
// failures return NaN which propagates through all arithmetic naturally.
// parse() checks Number.isFinite(result) to detect any failure.
// ---------------------------------------------------------------------------

class Parser {
  constructor(tokens, valueMap) {
    this.tokens = tokens;
    this.pos = 0;
    this.valueMap = valueMap;
  }

  peek() {
    return this.tokens[this.pos] || null;
  }

  consume(expected) {
    const tok = this.tokens[this.pos];
    if (!tok || tok.value !== expected) return null;
    this.pos++;
    return tok;
  }

  parse() {
    const result = this.expr();
    // Any failure (missing ref, div-by-zero, bad number) propagates as NaN/Infinity
    return Number.isFinite(result) ? result : null;
  }

  expr() {
    let left = this.term();
    while (true) {
      const tok = this.peek();
      if (!tok || (tok.value !== "+" && tok.value !== "-")) break;
      this.pos++;
      const right = this.term();
      left = tok.value === "+" ? left + right : left - right;
    }
    return left;
  }

  term() {
    let left = this.factor();
    while (true) {
      const tok = this.peek();
      if (!tok || (tok.value !== "*" && tok.value !== "/")) break;
      this.pos++;
      const right = this.factor();
      if (tok.value === "/") {
        // Division by zero → NaN (not 0); propagates to parse() returning null
        left = right === 0 ? NaN : left / right;
      } else {
        left = left * right;
      }
    }
    return left;
  }

  factor() {
    const tok = this.peek();
    if (tok && tok.type === "op" && (tok.value === "+" || tok.value === "-")) {
      this.pos++;
      const val = this.atom();
      return tok.value === "-" ? -val : val;
    }
    return this.atom();
  }

  atom() {
    const tok = this.peek();
    if (!tok) return NaN;

    if (tok.type === "number") {
      this.pos++;
      // NaN number token (from invalid literal like "100.50.30") propagates as NaN
      return tok.value;
    }

    if (tok.type === "code") {
      this.pos++;
      const val = this.valueMap[tok.value];
      if (val === undefined || val === null || val === "") return NaN;
      const num = typeof val === "number" ? val : parseFloat(String(val));
      return Number.isNaN(num) ? NaN : num;
    }

    if (tok.type === "op" && tok.value === "(") {
      this.pos++; // consume '('
      const val = this.expr();
      if (!this.consume(")")) return NaN; // unmatched paren
      return val;
    }

    return NaN; // unexpected token
  }
}

/**
 * Evaluate a formula string given a map of code → numeric value.
 * @param {string} formula    e.g. "(GHG001+GHG005+GHG007)/GE002"
 * @param {Object} valueMap   e.g. { GHG001: 100, GHG005: 50, ... }
 * @returns {number|null}     computed result, or null if any dependency missing / non-finite
 */
export function evaluateFormula(formula, valueMap) {
  if (!formula) return null;

  const tokens = tokenize(formula);
  if (tokens.length === 0) return null;

  const parser = new Parser(tokens, valueMap);
  return parser.parse();
}

// Straight-sum: tokens are only code refs joined by "+". No constants,
// no other operators, no parens, no anchored refs. Lets a partial sum
// (ignoring N/A deps) be safely meaningful where a partial product or
// ratio wouldn't be.
export function isStraightSumFormula(formula) {
  if (!formula) return false;
  const tokens = tokenize(formula);
  if (tokens.length === 0) return false;
  let codeCount = 0;
  for (const t of tokens) {
    if (t.type === "code") {
      if (t.value.endsWith("@baseline")) return false;
      codeCount++;
    } else if (t.type === "op" && t.value === "+") {
      continue;
    } else {
      return false;
    }
  }
  return codeCount > 0;
}

// Sum every finite dep in valueMap; ignore null/missing. Returns null
// when nothing finite was contributed.
export function evaluatePartialSum(formula, valueMap) {
  const refs = extractReferences(formula);
  let sum = 0;
  let any = false;
  for (const ref of refs) {
    const v = valueMap?.[ref];
    if (v == null || v === "") continue;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    if (!Number.isFinite(n)) continue;
    sum += n;
    any = true;
  }
  return any ? sum : null;
}

// ---------------------------------------------------------------------------
// Topological sort  — ensures dependents evaluate after their dependencies
// ---------------------------------------------------------------------------

/**
 * Build an evaluation order for calculated disclosures using topological sort.
 * @param {Object} formulaMap  { code: formula, ... }  e.g. from Disclosure docs
 * @returns {{ order: string[], error: string|null }}
 */
export function buildEvaluationOrder(formulaMap) {
  const codes = Object.keys(formulaMap);
  const codeSet = new Set(codes);
  const adj = new Map(); // code → codes it depends on (that are also calculated)
  for (const code of codes) {
    const deps = extractReferences(formulaMap[code]).filter((ref) =>
      codeSet.has(ref),
    );
    adj.set(code, deps);
  }

  const visited = new Set();
  const visiting = new Set(); // cycle detection
  const order = [];

  function dfs(code) {
    if (visited.has(code)) return true;
    if (visiting.has(code)) return false; // circular
    visiting.add(code);
    for (const dep of adj.get(code) || []) {
      if (!dfs(dep)) return false;
    }
    visiting.delete(code);
    visited.add(code);
    order.push(code);
    return true;
  }

  for (const code of codes) {
    if (!dfs(code)) {
      return {
        order: [],
        error: `Circular reference detected involving ${code}`,
      };
    }
  }

  return { order, error: null };
}
